import React, { useEffect, useState } from 'react';
import { Form, message, Select, TableProps } from 'antd';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { Button, Table, Image } from 'src/components';
import FormModalStyle from './SortOrdersStyle';
import { useAppDispatch, useAppSelector } from 'src/stores';
import { capitalizeFirstLetter } from 'src/utils';
import { FLAG_EN, FLAG_JA, FLAG_KO, FLAG_VI } from 'src/assets/images';
import {
  listOrderContentAction,
  sortOrderContentAction,
} from 'src/stores/screens/pageContent/pageContent.action';
import { LANGUAGE } from 'src/constants';
import styled from 'styled-components';
import { InputField } from 'src/components/form';

interface FormModalProps {
  isModalFormOpen: boolean;
  handleCancel: () => void;
  handleOk: () => void;
  sessionId?: string;
  fetchData: (page?: number) => void;
}

const FormModal: React.FC<FormModalProps> = ({
  isModalFormOpen,
  handleCancel,
  handleOk,
  sessionId,
  fetchData,
}) => {
  const dispatch = useAppDispatch();

  const { orders, isLoading } = useAppSelector((state) => state.pageContent);

  const [langue, setLangue] = useState(LANGUAGE.EN);

  useEffect(() => {
    getListContent();
  }, [sessionId]);

  const getListContent = async () => {
    if (sessionId) {
      await dispatch(listOrderContentAction({
        data: {
          page: 1,
          sessionId: sessionId || '',
          sorts: 'sort_order-asc,created_at-asc'
        }
      }));
    }
  };

  const validationSchema = yup.object().shape({
    orders: yup
      .array()
      .of(
        yup.object().shape({
          contentId: yup.string().required('Session ID is required'),
          sortOrder: yup
            .number()
            .typeError('Sort Order must be a number')
            .integer('Sort Order must be an integer')
            .min(0, 'Sort Order must be greater than or equal to 0')
            .max(100, 'Sort Order must be less than or equal to 100')
            .required('Sort Order is required'),
        })
      )
      .test(
        'unique-sortOrder',
        'Sort Order values must be unique (except 0)',
        (orders) => {
          if (!orders) return true;
          const nonZeroValues = orders
            .map((order) => order.sortOrder)
            .filter((value) => value !== 0);
          const uniqueValues = new Set(nonZeroValues);
          return nonZeroValues.length === uniqueValues.size;
        }
      ),
  });

  const initialValues: any = {
    sessionId: sessionId || '',
    orders:
      orders?.records?.length > 0
        ? orders.records.map((item) => ({
          contentId: item.id,
          sortOrder: item.sortOrder,
        }))
        : [],
  };

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues,
    onSubmit: async (values: any) => {
      const result: any = await dispatch(
        sortOrderContentAction({
          data: values,
        }))
      if (result?.payload?.error) {
        message.error(result?.payload?.message);
      } else {
        message.success('Setting order successful');
        formik.resetForm({});
        fetchData(1);
        handleOk();
      }
    },
  });

  const onClose = () => {
    formik.resetForm({
      values: {
        sessionId,
        orders: orders?.records?.map((item) => ({
          contentId: item.id,
          sortOrder: item.sortOrder,
        })) || [],
      },
    });
    handleCancel();
  };

  const onFilterLangue = (value: string) => {
    setLangue(value);
  };

  const langues = [
    {
      label: (
        <StyledWrapper>
          {' '}
          <Image src={FLAG_EN} /> EN{' '}
        </StyledWrapper>
      ),
      value: LANGUAGE.EN,
    },
    {
      label: (
        <StyledWrapper>
          {' '}
          <Image src={FLAG_VI} /> VI{' '}
        </StyledWrapper>
      ),
      value: LANGUAGE.VI,
    },
    {
      label: (
        <StyledWrapper>
          {' '}
          <Image src={FLAG_JA} /> JA{' '}
        </StyledWrapper>
      ),
      value: LANGUAGE.JA,
    },
    {
      label: (
        <StyledWrapper>
          {' '}
          <Image src={FLAG_KO} /> KO{' '}
        </StyledWrapper>
      ),
      value: LANGUAGE.KO,
    },
  ];

  const columns: TableProps<any>['columns'] = [
    {
      title: (
        <div className="langue-select">
          <div>Title</div>
          <div>
            <Select
              style={{ width: 120 }}
              placeholder="langue"
              maxTagCount="responsive"
              options={langues}
              value={langue}
              onChange={(value) => onFilterLangue(value)}
            />
          </div>
        </div>
      ),
      dataIndex: `title${capitalizeFirstLetter(langue)}`,
      key: `title${capitalizeFirstLetter(langue)}`,
      className: 'title',
      render: (titleEn: string) => {
        return (
          <p>
            {titleEn}
          </p>
        );
      },
    },
    {
      title: 'Description',
      dataIndex: `description${capitalizeFirstLetter(langue)}`,
      key: `description${capitalizeFirstLetter(langue)}`,
      render: (value) => <p>{value}</p>
    },
    {
      title: 'Order',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      className: 'sort-order',
      render: (_, record, index) => {
        return (
          <InputField
            className="form-control"
            field={formik.getFieldProps(`orders.${index}.sortOrder`)}
            setFieldValue={formik.setFieldValue}
            inputProps={{
              size: 'middle',
            }}
            error={formik.errors?.orders?.[index]?.sortOrder}
            touched={formik.touched?.orders?.[index]?.sortOrder}
          />
        );
      },
    }
  ];

  return (
    <FormModalStyle
      title={'Setting order'}
      visible={isModalFormOpen}
      onCancel={onClose}
      className="modal-form"
      width={800}
    >
      <Form
        name="edit-form"
        className="setting-form"
        layout="vertical"
        onFinish={() => {
          formik.handleSubmit();
        }}
      >
        {Array.isArray(formik.values.orders) && formik.values.orders.length > 0 ? (
          <div className="data-table">
            <Table dataSource={orders.records} columns={columns} scroll={{ x: 'auto', y: 600 }} />

            {formik.errors.orders && typeof formik.errors.orders === 'string' && (
              <div className="ant-form-item-explain-error" style={{ textAlign: 'right' }}>
                {formik.errors.orders}
              </div>
            )}
          </div>
        ) : (
          <div className='no-data'>No orders found</div>
        )}
        {/* Button footer */}
        <div className="button-footer">
          <Button buttonType="cancel" onClick={onClose} width={100}>
            Close
          </Button>
          <Button htmlType="submit" width={100} disabled={isLoading}>
            Setting
          </Button>
        </div>
      </Form>
    </FormModalStyle>
  );
};

const StyledWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export default FormModal;
